import { presenceColorForUser } from "../presence.js";
const SESSION_LABEL = /^Session ([0-9A-F]{8})-([0-9A-F]{8})$/;
const EMPTY_PROJECTION = Object.freeze({
    people: Object.freeze([]),
    sessions: Object.freeze([]),
    cursors: Object.freeze([]),
});
export class SecurePresenceProjectionError extends Error {
    constructor() {
        super("binding_protocol_violation");
        this.name = "SecurePresenceProjectionError";
    }
}
/** Stateful memoizer around the otherwise-pure trusted presence read model. */
export class SecurePresenceProjectionProjectorV1 {
    current = EMPTY_PROJECTION;
    project(snapshot) {
        const previousSessions = new Map(this.current.sessions.map((session) => [sessionKey(session), session]));
        const previousPeople = new Map(this.current.people.map((person) => [person.userId, person]));
        const sessions = [];
        for (const domain of snapshot.domains) {
            const presence = new Map(domain.presence.map((entry) => [entry.clientId, entry]));
            for (const binding of domain.bindings) {
                if (binding.state !== "bound")
                    continue;
                const entry = presence.get(binding.clientId);
                if (!entry ||
                    entry.bindingGeneration !== binding.bindingGeneration ||
                    entry.value === null) {
                    continue;
                }
                assertTrustedSessionLabelV1(entry.value.sessionLabel, binding.clientId);
                const key = `${binding.userId}\0${entry.value.sessionLabel}`;
                const previous = previousSessions.get(key);
                const cursor = projectCursor(binding.userId, entry, previous?.cursor);
                const candidate = Object.freeze({
                    clientId: binding.clientId,
                    sessionLabel: entry.value.sessionLabel,
                    userId: binding.userId,
                    baseRole: binding.baseRole,
                    isFacilitator: binding.isFacilitator,
                    cursor,
                    selection: entry.value.selection ?? null,
                    activeWidget: entry.value.activeWidget ?? null,
                    ...(entry.value.presenterViewport === undefined
                        ? {}
                        : { presenterViewport: entry.value.presenterViewport }),
                });
                sessions.push(previous && equalSession(previous, candidate) ? previous : candidate);
            }
        }
        sessions.sort(compareSessions);
        const immutableSessions = reuseArray(this.current.sessions, sessions);
        const cursors = immutableSessions.flatMap((session) => session.cursor ? [session.cursor] : []);
        const immutableCursors = reuseArray(this.current.cursors, cursors);
        const grouped = new Map();
        for (const session of immutableSessions) {
            const group = grouped.get(session.userId) ?? [];
            group.push(session);
            grouped.set(session.userId, group);
        }
        const people = [...grouped.entries()]
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([userId, personSessions]) => {
            const prior = previousPeople.get(userId);
            const immutablePersonSessions = Object.freeze([...personSessions]);
            if (prior && sameReferences(prior.sessions, immutablePersonSessions)) {
                return prior;
            }
            return Object.freeze({
                userId,
                sessions: immutablePersonSessions,
            });
        });
        const immutablePeople = reuseArray(this.current.people, people);
        if (immutablePeople === this.current.people &&
            immutableSessions === this.current.sessions &&
            immutableCursors === this.current.cursors) {
            return this.current;
        }
        this.current = Object.freeze({
            people: immutablePeople,
            sessions: immutableSessions,
            cursors: immutableCursors,
        });
        return this.current;
    }
    clear() {
        this.current = EMPTY_PROJECTION;
        return this.current;
    }
    snapshot() {
        return this.current;
    }
}
export function assertTrustedSessionLabelV1(sessionLabel, clientId) {
    if (!Number.isInteger(clientId) || clientId < 0 || clientId > 0xffff_ffff) {
        throw new SecurePresenceProjectionError();
    }
    const match = SESSION_LABEL.exec(sessionLabel);
    const expected = clientId.toString(16).padStart(8, "0").toUpperCase();
    if (!match || match[1] !== expected) {
        throw new SecurePresenceProjectionError();
    }
}
export function securePresenceRingV1(session) {
    if (session.baseRole === "owner")
        return "owner";
    return session.isFacilitator ? "facilitator" : null;
}
function projectCursor(userId, entry, previous) {
    const cursor = entry.value?.cursor;
    if (!cursor)
        return null;
    const candidate = Object.freeze({
        clientId: entry.clientId,
        sessionLabel: entry.value.sessionLabel,
        x: cursor.x,
        y: cursor.y,
        color: presenceColorForUser(userId),
    });
    return previous && equalCursor(previous, candidate) ? previous : candidate;
}
function compareSessions(left, right) {
    const user = left.userId.localeCompare(right.userId);
    if (user !== 0)
        return user;
    const ring = ringRank(left) - ringRank(right);
    if (ring !== 0)
        return ring;
    return left.sessionLabel.localeCompare(right.sessionLabel);
}
function ringRank(session) {
    return securePresenceRingV1(session) === "owner"
        ? 0
        : securePresenceRingV1(session) === "facilitator"
            ? 1
            : 2;
}
function sessionKey(session) {
    return `${session.userId}\0${session.sessionLabel}`;
}
function equalCursor(left, right) {
    return (left.clientId === right.clientId &&
        left.sessionLabel === right.sessionLabel &&
        left.x === right.x &&
        left.y === right.y &&
        left.color === right.color);
}
function equalSession(left, right) {
    return (left.clientId === right.clientId &&
        left.sessionLabel === right.sessionLabel &&
        left.userId === right.userId &&
        left.baseRole === right.baseRole &&
        left.isFacilitator === right.isFacilitator &&
        left.cursor === right.cursor &&
        left.selection === right.selection &&
        equalPresenterViewport(left.presenterViewport, right.presenterViewport) &&
        equalActiveWidget(left.activeWidget, right.activeWidget));
}
function equalPresenterViewport(left, right) {
    if (left === undefined || right === undefined)
        return left === right;
    if (left === null || right === null)
        return left === right;
    // PB-5441 d19: a presenter can change screen at the same camera numbers,
    // and its visible area with its pane size alone.
    return (left.x === right.x &&
        left.y === right.y &&
        left.zoom === right.zoom &&
        left.screen === right.screen &&
        left.width === right.width &&
        left.height === right.height);
}
function equalActiveWidget(left, right) {
    return (left === right ||
        (left !== null &&
            right !== null &&
            left.id === right.id &&
            left.mode === right.mode));
}
function reuseArray(previous, next) {
    return sameReferences(previous, next) ? previous : Object.freeze([...next]);
}
function sameReferences(left, right) {
    return (left.length === right.length &&
        left.every((entry, index) => entry === right[index]));
}
//# sourceMappingURL=presence-projection.js.map